import Typography from '@components/v2/ui/Typography/Typography';
import { TextField } from '@mui/material';
import { memo } from 'react';
import { isTouchEnabled } from '../../Theme';
import MobileProductInput from '../../modules/orders/products/product/grid/components/productInput/components/MobileProductInput';
import useGeneralSettingsStore from '../../stores/GeneralSettings/useGeneralSettingsStore';
import teceoInputConfig from '../../utils/TeceoInputConfig';
import configureGridItemWidth from '../../utils/configureGridItemsWidth';
import { OrderProductsTestIds } from '../../utils/testIds/OrderProducts.testIds';
import { ProductGridTestIds } from '../../utils/testIds/productGrid.testIds';
import { useProductQuantityInputStyles } from '../Product/useProductQuantityInputStyles';
import useProductGridInput from './hooks/useProductGridInput';
import { ProductGridInputProps } from './types/productGridInput.types';

const ProductGridInput = ({
  stock,
  maxValue,
  gridItem,
  disabled,
  showStock,
  inputProps,
  inputRef,
  textFieldProps,
  productColorAvailability,
  onEnter,
  onChangeGrid,
  tooltipMessage,
  hasTouchLayout = isTouchEnabled,
  isReadOnly = false,
}: ProductGridInputProps) => {
  const { classes: productClasses } = useProductQuantityInputStyles({
    isZeroValue: !gridItem.quantity,
  });
  const { textInputProps } = useProductGridInput({
    stock,
    maxValue,
    gridItem,
    disabled,
    showStock,
    inputProps,
    textFieldProps,
    productColorAvailability,
    onEnter,
    onChangeGrid,
  });

  const {
    generalSettings: { gridSize },
  } = useGeneralSettingsStore();

  return (
    <div
      test-id={`${ProductGridTestIds.GRID_CELL_QUANTITY}-${gridItem.sizeName}`}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        position: 'relative',
        width: `${configureGridItemWidth(gridSize)}px`,
      }}
    >
      <Typography
        variant="body2"
        noWrap
        align="center"
        sx={{ width: '100%' }}
        color="text.secondary"
        title={gridItem.sizeName}
      >
        {gridItem.sizeUnique ? 'U' : gridItem.sizeName}
      </Typography>
      {hasTouchLayout ? (
        <MobileProductInput
          disabled={!!disabled}
          isReadOnly={isReadOnly}
          stockValue={stock ?? undefined}
          showStock={showStock}
          inputRef={inputRef}
          textFieldProps={{ ...textInputProps }}
        />
      ) : (
        <>
          <TextField
            inputRef={inputRef}
            {...teceoInputConfig}
            {...textInputProps}
            className={productClasses.textField}
            title={tooltipMessage}
            tabIndex={1}
            fullWidth
            slotProps={{
              input: { disableInjectingGlobalStyles: true },
              htmlInput: {
                autoComplete: 'off',
                ...textInputProps.inputProps,
              },
            }}
          />

          {showStock && (
            <Typography
              variant="micro1"
              component="p"
              color="text.secondary"
              align="center"
              className={productClasses.quantity}
              noWrap
              testId={OrderProductsTestIds.GRID_STOCK_VALUE}
            >
              {stock ?? undefined}
            </Typography>
          )}
        </>
      )}
    </div>
  );
};

export default memo(ProductGridInput);
